import React, { useState, useEffect } from 'react';
import { toast } from '../Toast';
import { askConfirm } from '../ui';
import {
  CheckSquare, CheckCircle, Circle, Plus, Clock, User,
  Loader2, Trash2, AlertTriangle, Phone, Mail, Calendar,
  Flag, ChevronDown, X,
} from 'lucide-react';
import { commApi } from '../../services/crmApi';

interface Task {
  id: number;
  application_id: number;
  title: string;
  description: string | null;
  task_type: string;
  priority: string;
  status: string;
  due_date: string | null;
  assigned_to: number | null;
  assigned_name: string | null;
  completed_at: string | null;
  created_by_name: string | null;
  created_at: string;
}

const TASK_TYPES = [
  { value: 'todo', label: 'Zadanie', icon: CheckSquare, color: 'blue' },
  { value: 'call', label: 'Telefon', icon: Phone, color: 'orange' },
  { value: 'email', label: 'Email', icon: Mail, color: 'indigo' },
  { value: 'meeting', label: 'Spotkanie', icon: Calendar, color: 'teal' },
  { value: 'follow_up', label: 'Follow-up', icon: Clock, color: 'purple' },
];

const PRIORITIES = [
  { value: 'low', label: 'Niski', color: 'text-gray-400' },
  { value: 'normal', label: 'Normalny', color: 'text-blue-500' },
  { value: 'high', label: 'Wysoki', color: 'text-orange-500' },
  { value: 'urgent', label: 'Pilny', color: 'text-red-600' },
];

export default function TasksSection({ applicationId }: { applicationId: number }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState('todo');
  const [priority, setPriority] = useState('normal');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');

  const loadTasks = async () => {
    try {
      const data = await commApi.tasks(applicationId, filter !== 'all' ? filter : undefined);
      setTasks(data);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { loadTasks(); }, [applicationId, filter]);

  const handleCreate = async () => {
    if (!title.trim() || submitting) return;
    setSubmitting(true);
    try {
      await commApi.addTask(applicationId, {
        title: title.trim(),
        description: description || undefined,
        task_type: taskType,
        priority,
        due_date: dueDate || undefined,
      });
      setTitle(''); setDescription(''); setDueDate(''); setTaskType('todo'); setPriority('normal');
      setShowForm(false);
      loadTasks();
    } catch { toast.error('Błąd tworzenia zadania'); }
    setSubmitting(false);
  };

  const toggleComplete = async (task: Task) => {
    const newStatus = task.status === 'completed' ? 'open' : 'completed';
    await commApi.updateTask(task.id, { status: newStatus });
    loadTasks();
  };

  const deleteTask = async (id: number) => {
    if (!(await askConfirm({ title: 'Usuńac zadanie?' }))) return;
    await commApi.deleteTask(id);
    loadTasks();
  };

  const isOverdue = (task: Task) => {
    if (!task.due_date || task.status === 'completed') return false;
    return new Date(task.due_date) < new Date();
  };

  const formatDueDate = (d: string) => {
    const date = new Date(d);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = Math.ceil(diff / 86400000);
    if (days < 0) return `${Math.abs(days)}d temu`;
    if (days === 0) return 'Dzisiaj';
    if (days === 1) return 'Jutro';
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
  };

  const openCount = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled').length;
  const overdueCount = tasks.filter(t => isOverdue(t)).length;

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-blue-400" /></div>;

  return (
    <div>
      {/* Header stats */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-1.5 text-xs">
          <Circle size={12} className="text-blue-500" />
          <span className="font-medium">{openCount} otwartych</span>
        </div>
        {overdueCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-red-600">
            <AlertTriangle size={12} />
            <span className="font-medium">{overdueCount} po terminie</span>
          </div>
        )}
        <div className="flex-1" />
        {/* Filter */}
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="text-[10px] border rounded px-1.5 py-0.5 bg-white text-gray-600"
        >
          <option value="all">Wszystkie</option>
          <option value="open">Otwarte</option>
          <option value="completed">Zakonczone</option>
        </select>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          <Plus size={14} /> Nowe
        </button>
      </div>

      {/* New task form */}
      {showForm && (
        <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Co trzeba zrobic?"
            className="w-full text-sm border border-gray-200 rounded px-2.5 py-1.5 focus:ring-2 focus:ring-blue-200"
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) handleCreate(); }}
          />
          <div className="flex items-center gap-2 flex-wrap">
            <select value={taskType} onChange={e => setTaskType(e.target.value)} className="text-[11px] border rounded px-1.5 py-1 bg-white">
              {TASK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={priority} onChange={e => setPriority(e.target.value)} className="text-[11px] border rounded px-1.5 py-1 bg-white">
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className="text-[11px] border rounded px-1.5 py-1 bg-white"
            />
            <div className="flex-1" />
            <button onClick={() => setShowForm(false)} className="text-xs text-gray-400 px-2">Anuluj</button>
            <button
              onClick={handleCreate}
              disabled={!title.trim() || submitting}
              className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:bg-gray-300"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Dodaj
            </button>
          </div>
        </div>
      )}

      {/* Task list */}
      <div className="space-y-0.5">
        {tasks.map(task => {
          const typeInfo = TASK_TYPES.find(t => t.value === task.task_type) || TASK_TYPES[0];
          const TypeIcon = typeInfo.icon;
          const prioInfo = PRIORITIES.find(p => p.value === task.priority) || PRIORITIES[1];
          const overdue = isOverdue(task);
          const completed = task.status === 'completed';

          return (
            <div
              key={task.id}
              className={`flex items-center gap-2 py-2 px-2 rounded-lg group hover:bg-gray-50 transition-colors ${
                completed ? 'opacity-50' : ''
              } ${overdue ? 'bg-red-50/50' : ''}`}
            >
              {/* Complete toggle */}
              <button onClick={() => toggleComplete(task)} className="shrink-0">
                {completed ? (
                  <CheckCircle size={18} className="text-green-500" />
                ) : (
                  <Circle size={18} className="text-gray-300 hover:text-blue-500 transition-colors" />
                )}
              </button>

              {/* Type icon */}
              <TypeIcon size={14} className={`text-${typeInfo.color}-500 shrink-0`} />

              {/* Title + meta */}
              <div className="flex-1 min-w-0">
                <p className={`text-xs ${completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                  {task.title}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  {task.assigned_name && (
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                      <User size={9} /> {task.assigned_name}
                    </span>
                  )}
                  {task.created_by_name && (
                    <span className="text-[10px] text-gray-300">od {task.created_by_name}</span>
                  )}
                </div>
              </div>

              {/* Priority flag */}
              {task.priority !== 'normal' && (
                <Flag size={12} className={`${prioInfo.color} shrink-0`} />
              )}

              {/* Due date */}
              {task.due_date && (
                <span className={`text-[10px] shrink-0 ${overdue ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
                  {formatDueDate(task.due_date)}
                </span>
              )}

              {/* Delete */}
              <button
                onClick={() => deleteTask(task.id)}
                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded text-gray-300 hover:text-red-500 transition-all"
              >
                <Trash2 size={12} />
              </button>
            </div>
          );
        })}
      </div>

      {tasks.length === 0 && (
        <div className="text-center py-6 text-gray-400">
          <CheckSquare size={24} className="mx-auto mb-2 opacity-30" />
          <p className="text-xs">Brak zadan</p>
        </div>
      )}
    </div>
  );
}
